import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, setAuth, clearAuth, getAuthToken, consumePostLoginPath } from '@/lib/api';

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  isModerator: boolean;
}

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AdminUser | null>(null);
  // ⚠️ O estado inicial é DERIVADO do token, não `true` com correção depois.
  // Antes: `useState(true)` e, no efeito, `if (!token) setLoading(false)` —
  // `setState` síncrono dentro de efeito, que dispara um render em cascata
  // logo no primeiro quadro de quem não está autenticado. Apontado pelo
  // linter que o painel ganhou em 31/10/2026 (`react-hooks/set-state-in-effect`).
  const [loading, setLoading] = useState<boolean>(() => !!getAuthToken());

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    api.get('/auth/me')
      .then((r) => {
        const u = r.data?.data;
        if (!u?.isModerator && !u?.is_moderator) {
          clearAuth();
          navigate('/login');
          return;
        }
        setUser({
          id: u.id,
          username: u.username,
          email: u.email,
          isModerator: !!(u.isModerator || u.is_moderator),
        });
      })
      .catch(() => clearAuth())
      .finally(() => setLoading(false));
  }, [navigate]);

  async function login(username: string, password: string) {
    const { data } = await api.post('/auth/login', { username, password });
    const tokens = data?.data?.tokens;
    if (!tokens?.accessToken) throw new Error('Resposta inválida do servidor');
    // Limpa cache React Query de qualquer sessão anterior antes de trocar de
    // usuário. Sem isso, queries com queryKey igual (ex: ['admin-users','',''])
    // serviam dados/erros stale da sessão anterior por até staleTime (1min).
    queryClient.clear();
    setAuth(tokens.accessToken, tokens.refreshToken);
    const me = await api.get('/auth/me');
    const u = me.data?.data;
    if (!u?.isModerator && !u?.is_moderator) {
      clearAuth();
      throw new Error('Acesso restrito a moderadores.');
    }
    setUser({
      id: u.id,
      username: u.username,
      email: u.email,
      isModerator: true,
    });
    // Restaura path anterior à expiração da sessão. Se não há path preservado
    // (login direto), vai pra raiz.
    const postLoginPath = consumePostLoginPath();
    navigate(postLoginPath || '/');
  }

  function logout() {
    queryClient.clear();
    clearAuth();
    setUser(null);
    navigate('/login');
  }

  return { user, loading, login, logout };
}
